import { Package, CreditCard, ArrowRight } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentCompany } from '../../hooks/useCurrentCompany';

interface MenuOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  color: string;
}

const menuOptions: MenuOption[] = [
  {
    id: 'rollops',
    title: 'Manage RollOps Subscription',
    description: 'View and manage your RollOps SaaS subscription, trial status, and add-on services',
    icon: <CreditCard className="w-8 h-8 text-text-primary" />,
    route: '/subscriptions-plans/rollops',
    color: 'bg-[#9B1421]/20 border-[#9B1421]/50',
  },
  {
    id: 'gym',
    title: 'My Gym Subscriptions',
    description: 'Create and manage subscription plans for your gym members',
    icon: <Package className="w-8 h-8 text-text-primary" />,
    route: '/subscriptions-plans/gym',
    color: 'bg-blue-600/20 border-blue-600/50',
  },
];

export default function SubscriptionsPlansIndexPage() {
  const navigate = useNavigate();
  const { role } = useCurrentCompany();

  // Only owners and admins can access this page
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';

  if (!isOwnerOrAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-[#EBFFEE] mb-2">Access Denied</h2>
          <p className="text-[#A9A3A3]">Only owners and admins can access subscriptions and plans.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center">
              <Package size={20} className="text-text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#EBFFEE]">Subscriptions & Plans</h1>
              <p className="text-sm text-[#A9A3A3]">Manage your RollOps subscription and gym membership plans</p>
            </div>
          </div>
        </div>

        {/* Menu Options */}
        <div className="grid gap-6 md:grid-cols-2">
          {menuOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => navigate(option.route)}
              className={`
                text-left p-6 rounded-xl border-2 transition-all duration-200
                ${option.color}
                hover:border-opacity-100 hover:scale-[1.02]
                group
              `}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${option.id === 'rollops' ? 'bg-[#9B1421]/30' : 'bg-blue-600/30'}`}>
                  {option.icon}
                </div>
                <ArrowRight 
                  className="w-5 h-5 text-[#A9A3A3] group-hover:text-[#EBFFEE] group-hover:translate-x-1 transition-all" 
                />
              </div>
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-2">
                {option.title}
              </h3>
              <p className="text-sm text-[#A9A3A3] leading-relaxed">
                {option.description}
              </p>
            </button>
          ))}
        </div>
      </div>
  );
}
